import { Plus, Trash2, Wrench, Package, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumInput } from "@/components/NumInput";

export const VISITS = { 2: 6, 3: 4, 4: 3 };
const n = (v) => Number(v || 0);

export const maintCalc = (m) => {
  const v = m.visit || {};
  const perVisit = n(v.technicians) * n(v.mandays) * n(v.rate) + n(v.transport) + n(v.accommodation) + n(v.consumable);
  const others = (m.others || []).reduce((s, o) => s + n(o.amount), 0);
  const backupValue = (m.backup_items || []).reduce((s, b) => s + n(b.qty) * n(b.price), 0);
  const backupAnnual = backupValue / Math.max(n(m.contract_years) || 1, 1);
  const cont = n(m.contingency_pct) / 100;
  const options = [];
  Object.entries(VISITS).forEach(([p, vis]) => [true, false].forEach((b) => {
    const key = `${p}${b ? "b" : "n"}`;
    const visitCost = perVisit * vis, bk = b ? backupAnnual : 0, base = visitCost + others + bk;
    const hpp = Math.round(base * (1 + cont));
    const mg = m.margins?.[key];
    const auto = mg !== undefined && mg !== null && mg !== "";
    const price = auto ? Math.ceil(Number((hpp / (1 - Math.min(n(mg), 99) / 100)).toFixed(6))) : n(m.prices?.[key]);
    options.push({ key, period: Number(p), visits: vis, backup: b, visitCost, others, backupCost: bk, contingency: hpp - base, hpp, price, auto, profit: price - hpp, margin: price ? ((price - hpp) / price) * 100 : 0 });
  }));
  return { perVisit, others, backupValue, backupAnnual, options };
};

const Card = ({ title, Icon, children, action, testId }) => (
  <div className="card-premium p-6 fade-up" data-testid={testId}>
    <div className="mb-4 flex items-center justify-between gap-2">
      <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><Icon className="h-5 w-5 text-blue-800" />{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const Rows = ({ rows, cols, onChange, testPrefix, blank, addLabel }) => {
  const upd = (i, p) => onChange(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="tbl w-full text-sm">
          <thead><tr>{cols.map((c) => <th key={c.k} className={c.num ? "text-right" : ""}>{c.label}</th>)}<th className="w-10" /></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`${testPrefix}-row-${i}`}>
                {cols.map((c) => (
                  <td key={c.k} className={c.w}>
                    {c.num ? <NumInput className="h-8 bg-white text-right font-num" value={r[c.k] ?? ""} onChange={(e) => upd(i, { [c.k]: e.target.value })} data-testid={`${testPrefix}-${c.k}-${i}`} />
                      : c.calc ? <span className="block text-right font-num text-slate-700">{c.calc(r)}</span>
                        : <Input className="h-8 bg-white" list={c.list} placeholder={c.ph} value={r[c.k] ?? ""} onChange={(e) => upd(i, { [c.k]: e.target.value })} data-testid={`${testPrefix}-${c.k}-${i}`} />}
                  </td>
                ))}
                <td><Button size="icon" variant="ghost" onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`${testPrefix}-del-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button variant="outline" size="sm" className="mt-2 bg-white" onClick={() => onChange([...rows, { ...blank }])} data-testid={`${testPrefix}-add-btn`}><Plus className="h-4 w-4 mr-1" />{addLabel}</Button>
    </>
  );
};

const rp = (v) => `Rp ${Math.round(v || 0).toLocaleString("id-ID")}`;
const DEVICE_NAMES = ["Camera CCTV", "NVR", "DVR", "HDD 8TB", "Switch", "SFP Module", "PC Client", "Monitor"];

export const DevicesCard = ({ m, set }) => (
  <Card title="Daftar Perangkat yang di-Maintain" Icon={Layers} testId="maint-devices-card">
    <datalist id="maint-dev-names">{DEVICE_NAMES.map((d) => <option key={d} value={d} />)}</datalist>
    <p className="mb-3 text-xs text-slate-500">Kolom <b>Area</b> opsional. Jika diisi, PDF menampilkan tabel perangkat per area.</p>
    <Rows rows={m.devices || []} onChange={(v) => set({ devices: v })} testPrefix="maint-dev" addLabel="Tambah Perangkat" blank={{ area: "", name: "", qty: "", unit: "unit" }}
      cols={[{ k: "area", label: "Area (opsional)", ph: "Lobby / Lt. 1" }, { k: "name", label: "Perangkat", list: "maint-dev-names", ph: "Camera CCTV" }, { k: "qty", label: "Qty", num: true, w: "w-28" }, { k: "unit", label: "Unit", w: "w-28" }]} />
  </Card>
);

const F = ({ label, k, m, set, hint }) => (
  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}
    <NumInput className="mt-1 bg-white font-num" value={m.visit?.[k] ?? ""} onChange={(e) => set({ visit: { ...m.visit, [k]: e.target.value } })} data-testid={`maint-visit-${k}`} />
    {hint && <span className="mt-0.5 block text-[10px] normal-case text-slate-400">{hint}</span>}
  </label>
);

export const CostCard = ({ m, set, c }) => (
  <Card title="Cost per Kunjungan & Biaya Tahunan" Icon={Wrench} testId="maint-cost-card">
    <div className="grid gap-4 md:grid-cols-3">
      <F label="Jumlah Teknisi" k="technicians" m={m} set={set} />
      <F label="Mandays / Kunjungan" k="mandays" m={m} set={set} />
      <F label="Rate Teknisi / Hari" k="rate" m={m} set={set} />
      <F label="Transport / Kunjungan" k="transport" m={m} set={set} />
      <F label="Akomodasi / Allowance" k="accommodation" m={m} set={set} />
      <F label="Consumable (cleaning kit, dll)" k="consumable" m={m} set={set} />
    </div>
    <p className="mt-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-900" data-testid="maint-per-visit">Cost per kunjungan: <b className="font-num">{rp(c.perVisit)}</b></p>
    <h4 className="mt-5 mb-2 text-sm font-semibold text-slate-700">Biaya Tahunan Lain (on-call / standby, reporting, admin, dll)</h4>
    <Rows rows={m.others || []} onChange={(v) => set({ others: v })} testPrefix="maint-other" addLabel="Tambah Biaya" blank={{ name: "", amount: "" }}
      cols={[{ k: "name", label: "Keterangan", ph: "Standby on call" }, { k: "amount", label: "Nominal / Tahun", num: true, w: "w-48" }]} />
    <label className="mt-4 block w-48 text-xs font-semibold uppercase tracking-wide text-slate-500">Contingency %
      <NumInput className="mt-1 bg-white font-num" value={m.contingency_pct ?? ""} onChange={(e) => set({ contingency_pct: e.target.value })} data-testid="maint-contingency" />
    </label>
  </Card>
);

export const BackupCard = ({ m, set, c }) => (
  <Card title="Backup Unit (khusus opsi Backup)" Icon={Package} testId="maint-backup-card">
    <Rows rows={m.backup_items || []} onChange={(v) => set({ backup_items: v })} testPrefix="maint-bk" addLabel="Tambah Unit Backup" blank={{ name: "", type: "", qty: "", unit: "Unit", price: "" }}
      cols={[{ k: "name", label: "Item", ph: "Camera Dome 2 MP" }, { k: "type", label: "Merk / Type (opsional, tampil di PDF)", ph: "Hikvision DS-2CD1123G0E-I" }, { k: "qty", label: "Qty", num: true, w: "w-24" }, { k: "unit", label: "Unit", w: "w-24" }, { k: "price", label: "Harga / Unit", num: true, w: "w-40" }, { k: "sub", label: "Subtotal", calc: (r) => rp(n(r.qty) * n(r.price)), w: "w-36" }]} />
    <div className="mt-4 flex flex-wrap items-end gap-4">
      <label className="w-48 text-xs font-semibold uppercase tracking-wide text-slate-500">Masa Kontrak (tahun)
        <NumInput className="mt-1 bg-white font-num" value={m.contract_years ?? ""} onChange={(e) => set({ contract_years: e.target.value })} data-testid="maint-contract-years" />
      </label>
      <p className="text-sm text-slate-600" data-testid="maint-backup-annual">Nilai backup {rp(c.backupValue)} ÷ {Math.max(n(m.contract_years) || 1, 1)} tahun = <b className="font-num">{rp(c.backupAnnual)}</b> / tahun</p>
    </div>
  </Card>
);
